<script setup lang="ts">
    interface OnDemandButton {
        label?: string;
        title?: string;
        url?: string;
    }

    interface BuilderSpeaker {
        name?: string;
        avatar?: string;
    }

    interface BuilderSession {
        title?: string;
        url?: string;
        tags?: string;
        speakers?: BuilderSpeaker[];
        video?: { mp4?: string; webm?: string };
        poster?: string;
    }

    const props = withDefaults(
        defineProps<{
            headline?: string;
            subHead?: string;
            copy?: string;
            button?: OnDemandButton;
            treatAsHero?: boolean;
            sessions?: BuilderSession[];
        }>(),
        {
            headline: '',
            copy: '',
        }
    );

    const speaker1a = '/img/2026/on-demand/speaker-1a.png';
    const speaker1b = '/img/2026/on-demand/speaker-1b.png';
    const speaker2a = '/img/2026/on-demand/speaker-2a.png';
    const speaker2b = '/img/2026/on-demand/speaker-2b.png';
    const speaker3b = '/img/2026/on-demand/speaker-3b.png';
    const sampleMp4 = '/img/2026/on-demand/sample.mp4';
    const sampleWebm = '/img/2026/on-demand/sample.webm';
    const samplePoster = '/img/2026/on-demand/sample-poster.jpg';

    const defaultSessions = [
        {
            title: 'How AI Will Transform Business in the Next 18 Months',
            tags: ['AI', 'Trends', 'Product Lead Growth'],
            speakers: [
                { name: 'Yamini Rangan', avatar: speaker1a },
                { name: 'Dario Amodei', avatar: speaker1b },
            ],
            video: { mp4: sampleMp4, webm: sampleWebm },
            poster: samplePoster,
            url: 'https://www.youtube.com',
        },
        {
            title: 'The Impact of Making People Uncomfortable',
            tags: ['Customer Experience', 'Culture & Community', 'Coaching & Mentorship'],
            speakers: [
                { name: 'Stephen Cluskey', avatar: speaker2a },
                { name: 'Helen Russell', avatar: speaker2b },
            ],
            video: { mp4: sampleMp4, webm: sampleWebm },
            poster: samplePoster,
            url: 'https://www.youtube.com',
        },
        {
            title: 'HubSpot Spotlight',
            tags: ['Trends', 'AI', 'Coaching & Mentorship'],
            speakers: [
                { name: 'Yamini Rangan', avatar: speaker1a },
                { name: 'Andy Pitre', avatar: speaker3b },
            ],
            video: { mp4: sampleMp4, webm: sampleWebm },
            poster: samplePoster,
            url: 'https://www.youtube.com',
        },
        {
            title: 'Building Resilient Teams in a Changing World',
            tags: ['Leadership', 'Culture & Community'],
            speakers: [
                { name: 'Helen Russell', avatar: speaker2b },
                { name: 'Dario Amodei', avatar: speaker1b },
            ],
            video: { mp4: sampleMp4, webm: sampleWebm },
            poster: samplePoster,
            url: 'https://www.youtube.com',
        },
        {
            title: 'The Future of Customer-Centric Growth',
            tags: ['AI', 'Customer Experience', 'Trends'],
            speakers: [
                { name: 'Yamini Rangan', avatar: speaker1a },
                { name: 'Stephen Cluskey', avatar: speaker2a },
            ],
            video: { mp4: sampleMp4, webm: sampleWebm },
            poster: samplePoster,
            url: 'https://www.youtube.com',
        },
        {
            title: 'Scaling RevOps Without Losing the Human Touch',
            tags: ['Product Lead Growth', 'Leadership'],
            speakers: [
                { name: 'Andy Pitre', avatar: speaker3b },
                { name: 'Helen Russell', avatar: speaker2b },
            ],
            video: { mp4: sampleMp4, webm: sampleWebm },
            poster: samplePoster,
            url: 'https://www.youtube.com',
        },
        {
            title: 'From Burnout to Breakthrough: Leading With Empathy',
            tags: ['Culture & Community', 'Coaching & Mentorship'],
            speakers: [
                { name: 'Helen Russell', avatar: speaker2b },
                { name: 'Yamini Rangan', avatar: speaker1a },
            ],
            video: { mp4: sampleMp4, webm: sampleWebm },
            poster: samplePoster,
            url: 'https://www.youtube.com',
        },
        {
            title: 'Unlocking the Power of Connected Data',
            tags: ['AI', 'Product Lead Growth'],
            speakers: [
                { name: 'Dario Amodei', avatar: speaker1b },
                { name: 'Andy Pitre', avatar: speaker3b },
            ],
            video: { mp4: sampleMp4, webm: sampleWebm },
            poster: samplePoster,
            url: 'https://www.youtube.com',
        },
        {
            title: 'What Great Marketers Do Differently in 2026',
            tags: ['Trends', 'Customer Experience'],
            speakers: [
                { name: 'Stephen Cluskey', avatar: speaker2a },
                { name: 'Dario Amodei', avatar: speaker1b },
            ],
            video: { mp4: sampleMp4, webm: sampleWebm },
            poster: samplePoster,
            url: 'https://www.youtube.com',
        },
        {
            title: 'AI-Powered Sales: Beyond the Hype',
            tags: ['AI', 'Coaching & Mentorship', 'Trends'],
            speakers: [
                { name: 'Yamini Rangan', avatar: speaker1a },
                { name: 'Helen Russell', avatar: speaker2b },
            ],
            video: { mp4: sampleMp4, webm: sampleWebm },
            poster: samplePoster,
            url: 'https://www.youtube.com',
        },
        {
            title: 'Building Community at Scale',
            tags: ['Culture & Community', 'Leadership'],
            speakers: [
                { name: 'Andy Pitre', avatar: speaker3b },
                { name: 'Stephen Cluskey', avatar: speaker2a },
            ],
            video: { mp4: sampleMp4, webm: sampleWebm },
            poster: samplePoster,
            url: 'https://www.youtube.com',
        },
        {
            title: 'The Art of the Product Launch',
            tags: ['Product Lead Growth', 'Customer Experience'],
            speakers: [
                { name: 'Dario Amodei', avatar: speaker1b },
                { name: 'Yamini Rangan', avatar: speaker1a },
            ],
            video: { mp4: sampleMp4, webm: sampleWebm },
            poster: samplePoster,
            url: 'https://www.youtube.com',
        },
    ];

    const sessions = computed(() => {
        if (props.sessions?.length) {
            return props.sessions.map((s) => ({
                title: s.title || '',
                tags: s.tags ? s.tags.split(',').map((t: string) => t.trim()) : [],
                speakers: s.speakers || [],
                video: s.video?.mp4 ? s.video : null,
                poster: s.poster || '',
                url: s.url || '#',
            }));
        }
        return defaultSessions;
    });

    const trackRef = ref(null);
    const cardRefs = ref([]);
    const activeIndex = ref(0);
    const videoRefs = ref([]);
    const globalPaused = ref(false); // user toggled pause — stops all auto-play
    const hoveredIndex = ref(-1);
    const isAtStart = ref(true);
    const isAtEnd = ref(false);

    const prefersReducedMotion = ref(false);

    function scrollTo(direction) {
        const track = trackRef.value;
        if (!track) return;
        const card = track.querySelector('.on-demand-card');
        if (!card) return;
        const gap = parseFloat(getComputedStyle(track).gap) || 46;
        const scrollAmount = card.offsetWidth + gap;
        track.scrollBy({ left: direction === 'next' ? scrollAmount : -scrollAmount, behavior: 'smooth' });
    }

    function playVideo(index) {
        const video = videoRefs.value[index];
        if (!video || globalPaused.value || prefersReducedMotion.value) return;
        video
            .play()
            .then(() => updatePlayingState(index, true))
            .catch(() => {});
    }

    function pauseVideo(index) {
        const video = videoRefs.value[index];
        if (!video) return;
        video.pause();
        updatePlayingState(index, false);
    }

    function togglePause(index) {
        const video = videoRefs.value[index];
        if (!video) return;
        if (globalPaused.value) {
            // Resume: un-pause globally and play this video
            globalPaused.value = false;
            video
                .play()
                .then(() => updatePlayingState(index, true))
                .catch(() => {});
        } else {
            // Pause: stop all videos globally
            globalPaused.value = true;
            videoRefs.value.forEach((v, vi) => {
                if (v) {
                    v.pause();
                    updatePlayingState(vi, false);
                }
            });
        }
    }

    const playingState = reactive({});

    function updatePlayingState(index, playing) {
        playingState[index] = playing;
    }

    function isVideoPlaying(index) {
        return !!playingState[index];
    }

    function onHover(index) {
        hoveredIndex.value = index;
        if (index !== activeIndex.value) {
            pauseVideo(activeIndex.value);
            if (sessions.value[index].video) playVideo(index);
        }
    }

    function onLeave(index) {
        hoveredIndex.value = -1;
        if (index !== activeIndex.value) {
            pauseVideo(index);
            if (!globalPaused.value && sessions.value[activeIndex.value].video) playVideo(activeIndex.value);
        }
    }

    onMounted(() => {
        prefersReducedMotion.value = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        const track = trackRef.value;
        if (!track) return;

        const visibleCards = new Set();

        const observer = new IntersectionObserver(
            (entries) => {
                for (const entry of entries) {
                    const idx = Number(entry.target.dataset.index);
                    if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
                        visibleCards.add(idx);
                    } else {
                        visibleCards.delete(idx);
                        pauseVideo(idx);
                    }
                }
                // Active card is always the leftmost visible one
                if (visibleCards.size > 0) {
                    const leftmost = Math.min(...visibleCards);
                    const prev = activeIndex.value;
                    if (prev !== leftmost) {
                        pauseVideo(prev);
                        activeIndex.value = leftmost;
                    }
                    if (sessions.value[leftmost].video) playVideo(leftmost);
                    isAtStart.value = visibleCards.has(0);
                    isAtEnd.value = visibleCards.has(sessions.value.length - 1);
                }
            },
            { root: track, threshold: 0.5 }
        );

        nextTick(() => {
            cardRefs.value.forEach((card) => {
                if (card) observer.observe(card);
            });
        });
    });
</script>

<template>
    <section
        class="block-on-demand-sessions bg-[var(--pink)] flex flex-col gap-[40px] items-start px-[var(--box-padding-lr-m)] py-[50px] md:gap-[62px] md:px-[var(--box-padding-lr)] md:py-[90px]"
        aria-label="On demand sessions">
        <!-- Header -->
        <div class="flex flex-col gap-[18px] w-full max-w-[1440px]">
            <div class="flex flex-col gap-4 md:flex-row md:gap-[40px] md:items-center">
                <h2 class="font-hubspot-serif font-normal text-[36px] leading-[1.3] tracking-normal text-[var(--magenta)] md:text-[60px]">
                    {{ headline || 'Watch on demand sessions' }}
                </h2>
                <div v-if="button?.label" class="hidden md:display-block pt-[7px] shrink-0">
                    <a
                        :href="button.url || '#'"
                        :title="button.title"
                        class="inline-flex items-center gap-2.5 border border-(--magenta) rounded-full px-5 py-2.5 font-hubspot-serif text-[17px] leading-[1.4] tracking-[0.34px] text-(--magenta) no-underline transition-colors hover:bg-(--magenta) hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-(--magenta) focus-visible:ring-offset-2 focus-visible:ring-offset-(--pink)">
                        {{ button.label }}
                    </a>
                </div>
            </div>
            <div v-if="copy" class="font-hubspot-sans font-light text-[16px] leading-[26px] tracking-[0.32px] text-(--magenta) max-w-[785px]" v-html="copy" />
            <p v-else class="font-hubspot-sans font-light text-[16px] leading-[26px] tracking-[0.32px] text-(--magenta) max-w-[785px]">
                Couldn't make it live? Select Main Stage, HubSpot Heroes, and Education Sessions are on demand.
            </p>
        </div>

        <!-- Carousel Controls -->
        <div class="hidden md:flex items-center gap-[16px] border border-[var(--magenta)] rounded-full w-[76px] h-[42px] px-[16px] justify-center md:order-1">
            <button
                aria-label="Previous session"
                class="shrink-0 flex items-center justify-center transition-opacity text-[var(--magenta)] bg-transparent border-none p-0 cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--magenta)] rounded-sm"
                :class="isAtStart ? 'opacity-30 pointer-events-none' : ''"
                :aria-disabled="isAtStart"
                @click="scrollTo('prev')">
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
                    <path d="M13 7H1M1 7L7.14 0.5M1 7L7.14 13.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                </svg>
            </button>
            <button
                aria-label="Next session"
                class="shrink-0 flex items-center justify-center transition-opacity text-[var(--magenta)] bg-transparent border-none p-0 cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--magenta)] rounded-sm"
                :class="isAtEnd ? 'opacity-30 pointer-events-none' : ''"
                :aria-disabled="isAtEnd"
                @click="scrollTo('next')">
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
                    <path d="M1 7H13M13 7L6.86 0M13 7L6.86 14" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                </svg>
            </button>
        </div>

        <!-- Carousel -->
        <div
            ref="trackRef"
            role="region"
            aria-label="On demand session cards"
            aria-roledescription="carousel"
            class="on-demand-track flex gap-[var(--box-padding-lr-m)] overflow-x-auto snap-x snap-mandatory scroll-smooth md:gap-[46px] w-[calc(100%+var(--box-padding-lr-m)*2)] -mx-[var(--box-padding-lr-m)] px-[var(--box-padding-lr-m)] scroll-pl-[var(--box-padding-lr-m)] md:w-[calc(100%+var(--box-padding-lr)*2)] md:-mx-[var(--box-padding-lr)] md:px-[var(--box-padding-lr)] md:scroll-pl-[var(--box-padding-lr)]">
            <div
                v-for="(session, i) in sessions"
                :key="i"
                :ref="
                    (el) => {
                        cardRefs[i] = el;
                    }
                "
                :data-index="i"
                role="group"
                aria-roledescription="slide"
                :aria-label="`Session ${i + 1} of ${sessions.length}: ${session.title}`"
                class="on-demand-card flex flex-col gap-[28px] shrink-0 snap-start w-[calc(100vw-var(--box-padding-lr-m)*2)] md:w-auto max-w-[555px]">
                <!-- Thumbnail -->
                <a
                    :href="session.url"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="relative w-full rounded-[var(--radius)] overflow-hidden bg-[var(--magenta)] display-block focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--magenta)] focus-visible:ring-offset-4 focus-visible:ring-offset-[var(--pink)]"
                    @mouseenter="onHover(i)"
                    @mouseleave="onLeave(i)">
                    <video
                        v-if="session.video"
                        :ref="
                            (el) => {
                                videoRefs[i] = el;
                            }
                        "
                        :poster="session.poster"
                        muted
                        loop
                        playsinline
                        class="w-full h-auto display-block">
                        <source :src="session.video.webm" type="video/webm" />
                        <source :src="session.video.mp4" type="video/mp4" />
                    </video>
                    <MessImage v-else :image="{ src: session.poster }" lazy class="w-full h-auto display-block" />
                    <!-- Play/Pause button -->
                    <button
                        v-if="session.video"
                        :aria-label="isVideoPlaying(i) ? 'Pause video' : 'Play video'"
                        class="group absolute bottom-3 right-3 md:bottom-4 md:right-5 size-[31px] rounded-full bg-white text-[var(--magenta)] flex items-center justify-center transition-colors hover:bg-[var(--magenta)] hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
                        @click.stop.prevent="togglePause(i)">
                        <!-- Pause icon -->
                        <svg v-if="isVideoPlaying(i)" width="8" height="10" viewBox="0 0 8 10" fill="none" aria-hidden="true">
                            <rect width="2.5" height="10" rx="0.5" fill="currentColor" />
                            <rect x="5.5" width="2.5" height="10" rx="0.5" fill="currentColor" />
                        </svg>
                        <!-- Play icon -->
                        <svg v-else width="10" height="12" viewBox="0 0 10 12" fill="none" aria-hidden="true">
                            <path d="M1 1.5V10.5L9 6L1 1.5Z" fill="currentColor" stroke="currentColor" stroke-width="1" stroke-linejoin="round" />
                        </svg>
                    </button>
                </a>

                <!-- Content -->
                <div class="flex flex-col gap-[24px]">
                    <!-- Title + Tags -->
                    <div class="flex flex-col gap-[18px] md:gap-[23px] border-b border-[var(--dark-pink)] pb-[20px] md:pb-[26px]">
                        <h3 class="font-hubspot-serif font-normal text-[24px] md:text-[30px] leading-[1.3] tracking-normal text-[var(--magenta)]">
                            <MessLink
                                v-if="session.url && session.url !== '#'"
                                :href="session.url"
                                class="text-[var(--magenta)] leading-[1.3] no-underline hover:underline hover:decoration-dotted hover:decoration-[var(--dark-pink)] underline-offset-4"
                                >{{ session.title }}</MessLink
                            >
                            <span v-else>{{ session.title }}</span>
                        </h3>
                        <div class="flex flex-wrap gap-[8px]">
                            <span
                                v-for="tag in session.tags"
                                :key="tag"
                                class="border border-[var(--magenta)] rounded-full px-[10px] font-hubspot-sans font-normal text-[12px] leading-[26px] tracking-[0.24px] text-[var(--magenta)]">
                                {{ tag }}
                            </span>
                        </div>
                    </div>
                    <!-- Speakers -->
                    <div class="flex items-center gap-[32px]">
                        <div class="flex items-center isolate">
                            <div
                                v-for="(speaker, si) in session.speakers"
                                :key="si"
                                class="relative shrink-0 size-14 rounded-full bg-[var(--pink)] p-[2px]"
                                :class="si > 0 ? '-ml-[10px]' : ''"
                                :style="{ zIndex: session.speakers.length - si }">
                                <div class="w-full h-full rounded-full overflow-hidden">
                                    <MessImage :image="{ src: speaker.avatar, alt: speaker.name }" lazy class="w-full h-full object-cover" />
                                </div>
                            </div>
                        </div>
                        <p class="font-hubspot-serif text-[16px] leading-[1.4] tracking-[0.16px] text-[var(--magenta)]">
                            <template v-for="(speaker, si) in session.speakers" :key="si">
                                <span class="text-[var(--magenta)]">{{ speaker.name }}</span
                                ><template v-if="si < session.speakers.length - 1">, </template>
                            </template>
                        </p>
                    </div>
                </div>
            </div>
        </div>

        <!-- Mobile: controls + View All on same line -->
        <div class="flex md:hidden items-center gap-[16px] order-1">
            <div class="flex items-center gap-[16px] border border-[var(--magenta)] rounded-full w-[76px] h-[42px] px-[16px] justify-center shrink-0">
                <button
                    aria-label="Previous session"
                    class="shrink-0 flex items-center justify-center transition-opacity text-[var(--magenta)] bg-transparent border-none p-0 cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--magenta)] rounded-sm"
                    :class="isAtStart ? 'opacity-30 pointer-events-none' : ''"
                    :aria-disabled="isAtStart"
                    @click="scrollTo('prev')">
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
                        <path d="M13 7H1M1 7L7.14 0.5M1 7L7.14 13.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                    </svg>
                </button>
                <button
                    aria-label="Next session"
                    class="shrink-0 flex items-center justify-center transition-opacity text-[var(--magenta)] bg-transparent border-none p-0 cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--magenta)] rounded-sm"
                    :class="isAtEnd ? 'opacity-30 pointer-events-none' : ''"
                    :aria-disabled="isAtEnd"
                    @click="scrollTo('next')">
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
                        <path d="M1 7H13M13 7L6.86 0M13 7L6.86 14" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                    </svg>
                </button>
            </div>
            <MessLink
                v-if="button?.label"
                :href="button.url || '#'"
                class="inline-flex items-center gap-[10px] border border-[var(--magenta)] rounded-full px-[20px] h-[42px] font-hubspot-serif text-[16px] leading-[1.3] tracking-[0.32px] text-[var(--magenta)] no-underline transition-colors hover:bg-[var(--magenta)] hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--magenta)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--pink)]">
                {{ button.label }}
            </MessLink>
        </div>
    </section>
</template>

<style>
    /* Hide scrollbar on the carousel track */
    .on-demand-track {
        scrollbar-width: none;
        -ms-overflow-style: none;
    }
    .on-demand-track::-webkit-scrollbar {
        display: none;
    }

    /* Ensure last card has trailing space so it can snap */
    .on-demand-track::after {
        content: '';
        flex-shrink: 0;
        width: 1px;
    }

    @media (prefers-reduced-motion: reduce) {
        .on-demand-track {
            scroll-behavior: auto;
        }
        .block-on-demand-sessions video {
            display: none;
        }
        .block-on-demand-sessions .on-demand-card img {
            opacity: 1;
        }
    }
</style>
